import { z } from 'zod';
import { GrantTypeEnum } from 'repository/timekeeper/@types/TypeGrant';
import { TipoCalificadorEnum } from 'repository/timekeeper/@types/TypeCalificador';
import { ComparadorEnum } from 'repository/timekeeper/@types/TypeComparador';
import { FrequencyUnitTime } from 'repository/timekeeper/@types/TypeFrequencyUnitTime';
import { TipoEquivalenciaEnum } from 'repository/timekeeper/@types/TypeEquivalentia';
import { TipoProrrateoEnum } from 'repository/timekeeper/@types/TypePorrateo';
import { GrantConcessionEnum } from 'repository/timekeeper/@types/TypeGrantConcession';
import { TipoImpactoCambio } from 'repository/timekeeper/@types/TypeChangeImpact';
import { TipoCantidadPeriodoDefinidoEnum } from 'repository/timekeeper/@types/TypeCantidadPeriodoDefinidoEnum';
import { TipoFechaAniversarioEnum } from 'repository/timekeeper/@types/TypeFechaAniversario';

// Schema base para DatePattern (simplificado)
const DatePatternBaseSchema = z.object({
  // Agregar propiedades según sea necesario
}).passthrough();

// Schemas para Calificadores Create

const CalificadorCodigoDePagoCreateSchema = z.object({
  qualifier_type: z.literal(TipoCalificadorEnum.HORAS_CODIGO_DE_PAGO),
  interval_hour_amount: z.number().min(1).max(100),
  period_amount: z.number().min(1).max(100),
  period_unit_time: z.nativeEnum(FrequencyUnitTime),
  qualifier_comparator: z.nativeEnum(ComparadorEnum),
  qualifier_amount: z.number().min(0).max(1000),
  qualifier_paycode_id: z.number(),
});

const CalificadorDiasTrabajadosCreateSchema = z.object({
  qualifier_type: z.literal(TipoCalificadorEnum.DIAS_TRABAJADOS),
  interval_hour_amount: z.number().min(1).max(100),
  period_amount: z.number().min(1).max(100),
  period_unit_time: z.nativeEnum(FrequencyUnitTime),
  qualifier_amount: z.number().min(0).max(1000),
});

const CalificadorDiasComoEmpleadoCreateSchema = z.object({
  qualifier_type: z.literal(TipoCalificadorEnum.DIAS_COMO_EMPLEADO),
  interval_hour_amount: z.number().min(1).max(100),
  period_amount: z.number().min(1).max(100),
  period_unit_time: z.nativeEnum(FrequencyUnitTime),
  qualifier_amount: z.number().min(0).max(1000),
});

const CalificadorNingunoCreateSchema = z.object({
  qualifier_type: z.literal(TipoCalificadorEnum.NINGUNO),
  interval_hour_amount: z.number().min(1).max(100),
  period_amount: z.number().min(1).max(100),
  period_unit_time: z.nativeEnum(FrequencyUnitTime),
  qualifier_amount: z.number().min(0).max(1000),
});

// Union de Calificadores Create
const CalificadorCreateSchema = z.discriminatedUnion('qualifier_type', [
  CalificadorCodigoDePagoCreateSchema,
  CalificadorDiasTrabajadosCreateSchema,
  CalificadorDiasComoEmpleadoCreateSchema,
  CalificadorNingunoCreateSchema,
]);

// Schemas para Calificadores completos (con ID)

const CalificadorCodigoDePagoSchema = z.object({
  qualifier_type: z.literal(TipoCalificadorEnum.HORAS_CODIGO_DE_PAGO),
  interval_hour_amount: z.number().min(1).max(100),
  period_amount: z.number().min(1).max(100),
  period_unit_time: z.nativeEnum(FrequencyUnitTime),
  qualifier_comparator: z.nativeEnum(ComparadorEnum),
  qualifier_amount: z.number().min(0).max(1000),
  qualifier_paycode_id: z.number(),
  qualifier_grant_id: z.string(),
});

const CalificadorDiasTrabajadosSchema = z.object({
  qualifier_type: z.literal(TipoCalificadorEnum.DIAS_TRABAJADOS),
  interval_hour_amount: z.number().min(1).max(100),
  period_amount: z.number().min(1).max(100),
  period_unit_time: z.nativeEnum(FrequencyUnitTime),
  qualifier_amount: z.number().min(0).max(1000),
  qualifier_grant_id: z.string(),
});

const CalificadorDiasComoEmpleadoSchema = z.object({
  qualifier_type: z.literal(TipoCalificadorEnum.DIAS_COMO_EMPLEADO),
  interval_hour_amount: z.number().min(1).max(100),
  period_amount: z.number().min(1).max(100),
  period_unit_time: z.nativeEnum(FrequencyUnitTime),
  qualifier_amount: z.number().min(0).max(1000),
  qualifier_grant_id: z.string(),
});

const CalificadorNingunoSchema = z.object({
  qualifier_type: z.literal(TipoCalificadorEnum.NINGUNO),
  interval_hour_amount: z.number().min(1).max(100),
  period_amount: z.number().min(1).max(100),
  period_unit_time: z.nativeEnum(FrequencyUnitTime),
  qualifier_amount: z.number().min(0).max(1000),
  qualifier_grant_id: z.string(),
});

const CalificadorSchema = z.discriminatedUnion('qualifier_type', [
  CalificadorCodigoDePagoSchema,
  CalificadorDiasTrabajadosSchema,
  CalificadorDiasComoEmpleadoSchema,
  CalificadorNingunoSchema,
]);

// Schema para Grant Fijo

const GrantFijoSchema = z.object({
  id: z.number(),
  grant_id: z.number(),
  name: z.string().min(3).max(100),
  description: z.string().max(250).optional(),
  grant_type: z.literal(GrantTypeEnum.FIJO),
  qualifiers: z.array(CalificadorSchema),
  // FullTimeEquivalentConfig
  full_time_equivalents: z.nativeEnum(TipoEquivalenciaEnum),
  paycode_id: z.number().optional(),
  // VencimientoConfig
  expiration: z.boolean(),
  expiration_number: z.number(),
  expiration_unit_date: z.nativeEnum(FrequencyUnitTime),
  // ProrrateoConfig
  apportionment_type: z.nativeEnum(TipoProrrateoEnum).optional(),
  amount_type: z.nativeEnum(TipoCantidadPeriodoDefinidoEnum).optional(),
  anniversary_date_type: z.nativeEnum(TipoFechaAniversarioEnum).optional(),
  // ConcesionConfigFijo
  date_pattern: DatePatternBaseSchema,
  grant_concession: z.nativeEnum(GrantConcessionEnum),
  impact_change_type: z.nativeEnum(TipoImpactoCambio),
  impact_recalculate: z.boolean(),
});

// Schema para Grant Percibido

const GrantPercibidoSchema = z.object({
  id: z.number(),
  grant_id: z.number(),
  name: z.string().min(3).max(100),
  description: z.string().max(250).optional(),
  grant_type: z.literal(GrantTypeEnum.PERCIBIDO),
  qualifiers: z.array(CalificadorSchema),
  // VencimientoConfig
  expiration: z.boolean(),
  expiration_number: z.number().positive().max(100),
  expiration_unit_date: z.nativeEnum(FrequencyUnitTime),
  // ConcesionConfigPercibido
  paycode_id: z.number(),
  date_pattern: DatePatternBaseSchema.optional(),
});

// Union de Grant Fijo y Grant Percibido
export const GrantSchema = z.discriminatedUnion('grant_type', [
  GrantFijoSchema,
  GrantPercibidoSchema,
]);

// Schema para Grant Fijo Create

export const GrantFijoCreateSchema = z.object({
  name: z.string().min(3).max(100),
  description: z.string().max(250).optional(),
  grant_type: z.literal(GrantTypeEnum.FIJO),
  qualifiers: z.array(CalificadorCreateSchema),
  // FullTimeEquivalentConfig
  full_time_equivalents: z.nativeEnum(TipoEquivalenciaEnum),
  paycode_id: z.number().optional(),
  // VencimientoConfig
  expiration: z.boolean(),
  expiration_number: z.number().positive().max(100),
  expiration_unit_date: z.nativeEnum(FrequencyUnitTime),
  // ProrrateoConfig
  apportionment_type: z.nativeEnum(TipoProrrateoEnum).optional(),
  amount_type: z.nativeEnum(TipoCantidadPeriodoDefinidoEnum).optional(),
  anniversary_date_type: z.nativeEnum(TipoFechaAniversarioEnum).optional(),
  // ConcesionConfigFijo
  date_pattern: DatePatternBaseSchema,
  grant_concession: z.nativeEnum(GrantConcessionEnum),
  impact_change_type: z.nativeEnum(TipoImpactoCambio),
  impact_recalculate: z.boolean(),
});

// Schema para Grant Percibido Create

export const GrantPercibidoCreateSchema = z.object({
  name: z.string().min(3).max(100),
  description: z.string().max(250).optional(),
  grant_type: z.literal(GrantTypeEnum.PERCIBIDO),
  qualifiers: z.array(CalificadorCreateSchema),
  // VencimientoConfig
  expiration: z.boolean(),
  expiration_number: z.number().positive().max(100),
  expiration_unit_date: z.nativeEnum(FrequencyUnitTime),
  // ConcesionConfigPercibido
  paycode_id: z.number(),
  date_pattern: DatePatternBaseSchema.optional(),
});

// Union de Grant Create
export const GrantCreateSchema = z.discriminatedUnion('grant_type', [
  GrantFijoCreateSchema,
  GrantPercibidoCreateSchema,
]);
